/**
 * Inline form for registering a new MCP server.
 *
 * Validates client-side that:
 *   - name is non-empty (1..80)
 *   - url is HTTPS (server still re-validates via the egress guard)
 *   - credential.token is present when auth_scheme !== 'none'
 *   - credential.header is present when auth_scheme === 'api_key_header'
 *
 * On submit posts to /api/mcp-servers; on success bubbles the registered
 * server + its discovered tools up to the parent via `onCreated`.
 * Discovery failures from the server show inline.
 *
 * Tools arrive disabled unless the admin ticked "select all", which turns
 * them on and grants every agent access (the box used to only do
 * the second half, so ticking it changed nothing an agent could use).
 */
import { useState } from "react";
import { Button } from "../../ui/button.js";
import { Input } from "../../ui/input.js";
import { FormField } from "../../ui/form-field.js";
import { Checkbox } from "../../ui/checkbox.js";
import { Label } from "../../ui/label.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select.js";
import type {
  McpAuthScheme,
  McpRegisterPayload,
  McpRegisterResponse,
} from "./types.js";

interface FormState {
  name: string;
  url: string;
  authScheme: McpAuthScheme;
  token: string;
  header: string;
  agentAccessAll: boolean;
  submitting: boolean;
  error: string | null;
}

const INITIAL: FormState = {
  name: "",
  url: "",
  authScheme: "none",
  token: "",
  header: "",
  agentAccessAll: false,
  submitting: false,
  error: null,
};

export interface McpServerFormProps {
  onCreated?: (input: McpRegisterResponse) => void;
  onCancel?: () => void;
  /** The form's id, so a sheet footer outside it can submit it. */
  formId?: string;
  /** In a sheet: no glass of its own and no buttons; the footer submits. */
  bare?: boolean;
  /** Told whenever a submit starts or settles. */
  onSubmittingChange?: (submitting: boolean) => void;
}

export function McpServerForm({ onCreated, onCancel, formId, bare = false, onSubmittingChange }: McpServerFormProps) {
  const [s, setState] = useState<FormState>(INITIAL);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (s.submitting) return;
    update("error", null);

    if (!s.name.trim()) return update("error", "Name is required");
    if (!s.url.trim().startsWith("https://"))
      return update("error", "URL must use https://");
    if (s.authScheme !== "none" && !s.token.trim())
      return update("error", "Token is required for the chosen auth scheme");
    if (s.authScheme === "api_key_header" && !s.header.trim())
      return update("error", "Header is required for api_key_header auth");

    update("submitting", true);
    onSubmittingChange?.(true);
    const payload: McpRegisterPayload = {
      name: s.name.trim(),
      url: s.url.trim(),
      authScheme: s.authScheme,
      ...(s.authScheme === "none"
        ? {}
        : {
            credential: {
              token: s.token.trim(),
              ...(s.authScheme === "api_key_header"
                ? { header: s.header.trim() }
                : {}),
            },
          }),
      agentAccessAll: s.agentAccessAll,
    };
    try {
      const res = await fetch("/api/mcp-servers", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Registration failed (${res.status})`);
      }
      const body = (await res.json()) as McpRegisterResponse;
      setState(INITIAL);
      onCreated?.(body);
    } catch (err) {
      update("error", err instanceof Error ? err.message : "Unknown error");
    } finally {
      setState((prev) => ({ ...prev, submitting: false }));
      onSubmittingChange?.(false);
    }
  }

  return (
    <form
      id={formId}
      onSubmit={submit}
      className={bare ? "flex flex-col gap-md" : "material-glass flex flex-col gap-md p-md"}
      data-testid="mcp-server-form"
    >
      <FormField label="Name" htmlFor="mcp-name">
        <Input
          id="mcp-name"
          type="text"
          value={s.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Linear (production)"
          maxLength={80}
          required
        />
      </FormField>

      <FormField label="Server URL" htmlFor="mcp-url" hint="HTTPS only.">
        <Input
          id="mcp-url"
          type="url"
          value={s.url}
          onChange={(e) => update("url", e.target.value)}
          placeholder="https://mcp.example.com/sse"
          required
        />
      </FormField>

      <FormField
        label="Auth scheme"
        htmlFor="mcp-auth"
        hint="For token schemes, use a scoped service token, not your personal API key. Everyone in this workspace, including Agents, acts with this credential."
      >
        <Select
          value={s.authScheme}
          onValueChange={(v) => update("authScheme", v as McpAuthScheme)}
        >
          <SelectTrigger id="mcp-auth">
            <SelectValue placeholder="Choose auth scheme" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None (public server)</SelectItem>
            <SelectItem value="bearer">Bearer token</SelectItem>
            <SelectItem value="api_key_header">API key header</SelectItem>
          </SelectContent>
        </Select>
      </FormField>

      {s.authScheme !== "none" && (
        <FormField label="Token" htmlFor="mcp-token">
          <Input
            id="mcp-token"
            type="password"
            value={s.token}
            onChange={(e) => update("token", e.target.value)}
            placeholder="…"
            autoComplete="off"
            required
          />
        </FormField>
      )}

      {s.authScheme === "api_key_header" && (
        <FormField label="Header name" htmlFor="mcp-header">
          <Input
            id="mcp-header"
            type="text"
            value={s.header}
            onChange={(e) => update("header", e.target.value)}
            placeholder="X-API-Key"
            maxLength={80}
            required
          />
        </FormField>
      )}

      <div className="flex items-center gap-sm text-sm">
        <Checkbox
          id="mcp-grant-all"
          checked={s.agentAccessAll}
          onCheckedChange={(c) => update("agentAccessAll", Boolean(c))}
        />
        <Label htmlFor="mcp-grant-all" className="text-sm">
          Enable all discovered tools and grant every agent access. Leave this
          off to review each tool before turning it on.
        </Label>
      </div>

      {s.error && (
        <div role="alert" className="text-sm text-[var(--error)]">
          {s.error}
        </div>
      )}

      {!bare && (
      <div className="flex gap-sm justify-end">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="text-xs"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          size="sm"
          className="text-xs"
          loading={s.submitting}
        >
          Register MCP server
        </Button>
      </div>
      )}
    </form>
  );
}
